import { FC, Suspense } from 'react';
import { lazily } from 'react-lazily';
import { createBrowserRouter, Navigate, RouterProvider } from 'react-router-dom';

import { Box } from '@bonusly/design-system';
import { assertPresence } from '@bonusly/runtime';

import { AdminAppLayout } from '../components/layout/AdminAppLayout';
import { AppRootLayout } from '../components/layout/AppRootLayout';
import { RootLayout } from '../components/layout/root-layout';
import { FlaggedRoute } from '../components/routes/FlaggedRoute';
import { RequireAdmin } from '../components/routes/require-admin';
import { RouteError } from '../components/routes/RouteError';
import { MobileUnavailable } from '../components/routes/statuses/MobileUnavailable';
import { PageNotFound } from '../components/routes/statuses/PageNotFound';
import { Variant } from '../lib/who-am-i/lib/experiment';
import * as adminRewardsLoaders from '../modules/admin-rewards/loaders';
import { loadRecognitionScoresData } from '../modules/reports/recognition-scores/loaders';
import { loadRewardsHistory, loadRewardsIndex } from '../modules/rewards/loaders';
import { loadAward } from '../queries/awards';
import { loadCelebration, loadCelebrationMetadata, loadInactiveCelebrations } from '../queries/celebrations';
import { loadCustomPropertiesUserGroups, loadUserGroups } from '../queries/groups';
import { loadAdminIncentive } from '../queries/incentives';
import { loadAdminNominationPeriod } from '../queries/nominations';
import { loadSubscriptions } from '../queries/subscriptions';

// Lazily loaded module collections. These are grouped to reduce the number of HTTP requests on app load
const Admin = lazily(() => import('../modules/admin'));
const Awards = lazily(() => import('../modules/awards'));
const Celebrations = lazily(() => import('../modules/celebrations'));
const Growth = lazily(() => import('../modules/growth'));
const User = lazily(() => import('../modules/user'));
const Incentives = lazily(() => import('../modules/incentives'));
const Nominations = lazily(() => import('../modules/nominations'));
const Profile = lazily(() => import('../modules/profile'));
const Recognition = lazily(() => import('../modules/recognition'));
const Reports = lazily(() => import('../modules/reports'));
const Rewards = lazily(() => import('../modules/rewards'));
const SuperAdmin = lazily(() => import('../modules/super-admin'));
const AdminKyb = lazily(() => import('../modules/admin-kyb'));
const Meet = lazily(() => import('../modules/meet'));
const Spotlight = lazily(() => import('../modules/spotlight'));
const Bizy = lazily(() => import('../modules/bizy'));

// Widget components - lazy loaded to avoid bloating main bundle
const { WidgetLayout } = lazily(() => import('../components/layout/widget-layout'));
const { WidgetPage } = lazily(() => import('../components/widget/widget-page'));

const router = createBrowserRouter([
	{
		path: '',
		element: <RootLayout />,
		errorElement: <RouteError />,
		children: [
			{
				path: 'admin',
				element: <AdminAppLayout />,
				children: [
					{
						path: 'company/*',
						element: <Admin.Company />,
					},
					{
						path: 'dashboard/*',
						element: <Admin.Dashboard />,
					},
					{
						path: 'growth/*',
						element: <Admin.Growth />,
					},
					{
						path: 'incentives/*',
						element: <Incentives.IncentivesSummary />,
						children: [
							{
								path: 'summary',
								element: <Incentives.IncentivesSummary />,
							},
						],
					},
					{
						path: 'subscriptions',
						element: <Admin.ReportSubscriptions />,
						loader: () => loadSubscriptions(),
					},
					{
						path: 'reports/collaboration',
						element: (
							<RequireAdmin require={['finance', 'global']}>
								<Reports.CollaborationReport />
							</RequireAdmin>
						),
					},
					{
						path: 'reports/participation',
						element: <Reports.ParticipationReport />,
						children: [
							{
								path: 'giving-and-receiving',
								element: <Reports.ParticipationReport />,
							},
							{
								path: 'managers-and-teams',
								element: <Reports.ParticipationReport />,
							},
							{
								path: 'compare',
								element: <Reports.ParticipationReport />,
							},
						],
					},
					{
						path: 'programs/awards',
						element: <Awards.AwardsRoot />,
						children: [
							{
								path: '',
								element: <Awards.AwardsIndex />,
								loader: () => {
									return loadCustomPropertiesUserGroups();
								},
							},
							{
								path: 'new',
								element: <Awards.CreateAward />,
								loader: () => {
									return loadCustomPropertiesUserGroups();
								},
							},
							{
								path: ':id/edit',
								element: <Awards.EditAward />,
								loader: ({ params }) => {
									assertPresence(params.id);
									return Promise.all([loadAward(params.id), loadCustomPropertiesUserGroups()]);
								},
							},
							{
								path: 'archived',
								element: <Awards.AwardsArchivedIndex />,
								loader: () => {
									return loadCustomPropertiesUserGroups();
								},
							},
						],
					},
					{
						path: 'programs/celebrations',
						element: <Celebrations.CelebrationsRoot />,
						children: [
							{
								path: '',
								element: <Celebrations.CelebrationsIndex />,
								loader: () => {
									return loadInactiveCelebrations();
								},
							},
							{
								path: 'new/:type',
								element: <Celebrations.CreateCelebration />,
								loader: ({ params }) => {
									assertPresence(params.type);
									return Promise.all([loadUserGroups(), loadCelebrationMetadata({ celebration_type: params.type })]);
								},
							},
							{
								path: ':id/edit',
								element: <Celebrations.EditCelebration />,
								loader: ({ params }) => {
									assertPresence(params.id);
									return Promise.all([loadCelebration(params.id), loadUserGroups(), loadCelebrationMetadata({ celebration_id: params.id })]);
								},
							},
						],
					},
					{
						path: 'programs/nominations',
						element: <Nominations.NominationsRoot />,
						children: [
							{
								path: '',
								element: <Nominations.NominationPeriodsIndex />,
							},
							{
								path: 'new',
								element: <Nominations.CreateNominationPeriod />,
								loader: () => loadUserGroups(),
							},
							{
								path: ':id',
								element: <Nominations.AdminNominationPeriod />,
								loader: ({ params }) => {
									assertPresence(params.id);
									return loadAdminNominationPeriod(params.id);
								},
							},
							{
								path: ':id/edit',
								element: <Nominations.EditNominationPeriod />,
								loader: ({ params }) => {
									assertPresence(params.id);
									return Promise.all([loadAdminNominationPeriod(params.id), loadUserGroups()]);
								},
							},
						],
					},
					{
						path: 'programs/incentives',
						element: <Incentives.IncentivesRoot />,
						children: [
							{
								path: '',
								element: <Incentives.IncentivesIndex />,
								loader: () => {
									return loadCustomPropertiesUserGroups();
								},
							},
							{
								path: 'new',
								element: <Incentives.CreateIncentive />,
								loader: () => {
									return loadCustomPropertiesUserGroups();
								},
							},
							{
								path: ':id/edit',
								element: <Incentives.EditIncentive />,
								loader: ({ params }) => {
									assertPresence(params.id);
									return Promise.all([loadAdminIncentive(params.id), loadCustomPropertiesUserGroups()]);
								},
							},
							{
								path: 'archived',
								element: <Incentives.IncentivesArchivedIndex />,
								loader: () => {
									return loadCustomPropertiesUserGroups();
								},
							},
						],
					},
					{
						path: 'programs/*',
						element: <Admin.Programs />,
					},
					{
						path: 'reports/recognition_scores',
						element: (
							<RequireAdmin require="finance">
								<Reports.RecognitionScores />
							</RequireAdmin>
						),
						loader: loadRecognitionScoresData,
					},
					{
						path: 'reports/pulse-results',
						element: (
							<RequireAdmin require={['finance', 'global']}>
								<Reports.CheckInsResults />
							</RequireAdmin>
						),
					},
					{
						path: 'reports/points-activity',
						element: (
							<RequireAdmin require={['finance', 'global']}>
								<Reports.PointsActivityReport />
							</RequireAdmin>
						),
						children: [
							{
								path: 'summary',
								element: <Reports.PointsActivityReport />,
							},
							{
								path: 'programs',
								element: <Reports.PointsActivityReport />,
							},
							{
								path: 'reward-balance',
								element: <Reports.PointsActivityReport />,
							},
						],
					},
					{
						path: 'rewards',
						element: <Admin.AdminRewardsRoot />,
						children: [
							{
								path: 'catalog',
								element: <Admin.RewardsCatalogIndex />,
								loader: adminRewardsLoaders.loadIndex,
							},
							{
								path: 'collections/exclusive',
								element: <Admin.ExclusiveItems />,
								loader: adminRewardsLoaders.loadExclusiveItems,
							},
							{
								path: 'collections/swag',
								element: <Admin.SwagItems />,
								loader: adminRewardsLoaders.loadSwagItems,
							},
							{
								path: 'collections/:id',
								element: <Admin.RewardsCollectionDetail />,
								loader: adminRewardsLoaders.loadCollectionDetail,
							},
							{
								path: 'exclusive/new',
								element: <Admin.CreateExclusiveReward />,
							},
							{
								path: 'exclusive/:id/edit',
								element: <Admin.EditExclusiveReward />,
								loader: adminRewardsLoaders.loadEditExclusiveReward,
							},
							{
								path: 'items',
								element: <Admin.RewardsManageItems />,
								loader: adminRewardsLoaders.loadItems,
							},
							{
								path: 'settings',
								element: <Admin.RewardsSettings />,
								loader: adminRewardsLoaders.loadSettings,
							},
						],
					},

					{
						path: '*',
						element: (
							<Box p={6}>
								<PageNotFound />
							</Box>
						),
					},
				],
			},
			{
				path: 'super_admin',
				element: <SuperAdmin.SuperAdminAppLayout />,
				children: [
					{
						path: 'kvms/recognition/giving-rate',
						element: <SuperAdmin.RecognitionGivingRate />,
					},
					{
						path: 'kvms/rewards/redemptions-per-user',
						element: <SuperAdmin.RedemptionsPerUser />,
					},
					{
						path: 'kvms/one-on-one',
						element: <SuperAdmin.OneOnOneKvms />,
					},
					{
						path: 'kvms/pulse-checks',
						element: <SuperAdmin.PulseChecksKvms />,
					},
					{
						path: 'kvms/insights',
						element: <SuperAdmin.InsightsDashboard />,
					},
					{
						path: 'kvms/one-on-one-activation',
						element: <SuperAdmin.OneOnOneActivationDashboard />,
					},
					{
						path: 'kvms/transcript-metrics',
						element: <SuperAdmin.TranscriptMetricsDashboard />,
					},
					{
						path: 'kvms/trial-conversion',
						element: <SuperAdmin.TrialConversionDashboard />,
					},
					{
						path: 'kvms/bizy',
						element: <SuperAdmin.BizyDashboard />,
					},
					{
						path: 'kyb',
						element: <AdminKyb.KybPage />,
					},
					{
						path: 'bizy/conversations',
						element: <SuperAdmin.BizyConversations />,
					},
					{
						path: 'bizy/conversations/:conversationId',
						element: <SuperAdmin.BizyConversationDetail />,
					},
					{
						path: 'bizy/feedback',
						element: <SuperAdmin.BizyFeedback />,
					},
					// to use SuperAdmin's layout but still show the 404 page
					{ path: '*', element: <PageNotFound /> },
				],
			},

			// Print-friendly and shared pages without layout
			{
				path: 'p',
				children: [
					{
						path: 'recap-review/*',
						element: (
							<Suspense>
								<Growth.RecapReviewPrintView />
							</Suspense>
						),
					},
				],
			},

			// Widget page for iframe embedding without layout
			{
				path: 'widget',
				element: <WidgetLayout />,
				children: [
					{
						path: '',
						element: <WidgetPage />,
					},
				],
			},

			// Shareable routes without layout
			{
				path: 'share',
				children: [
					{
						path: 'nominations/:id',
						element: (
							<Suspense>
								<Nominations.PublicNominationPeriod />
							</Suspense>
						),
						loader: ({ params }) => {
							assertPresence(params.id);
							return loadAdminNominationPeriod(params.id);
						},
					},
				],
			},

			{
				path: 'spotlight/employee/:spotlightId',
				element: (
					<FlaggedRoute variant={Variant.EmployeeSpotlight}>
						<Spotlight.UserSpotlightViewerPage />
					</FlaggedRoute>
				),
			},

			// Legacy route - redirect to new path for backward compatibility
			{
				path: 'spotlight/:spotlightId',
				element: (
					<FlaggedRoute variant={Variant.EmployeeSpotlight}>
						<Spotlight.LegacySpotlightRedirect />
					</FlaggedRoute>
				),
			},

			// Redirect auth routes to home - these are handled by Rails, not the React app
			// Users who navigate directly to these URLs on the React app should be redirected
			{ path: 'users/sign_in', element: <Navigate to="/" replace /> },
			{ path: 'users/sign_in_start', element: <Navigate to="/" replace /> },
			{ path: 'sign-in', element: <Navigate to="/" replace /> },

			// The rest of the app uses the AppRootLayout
			{
				path: '',
				element: <AppRootLayout />,
				children: [
					{
						path: '',
						element: <Recognition.BonusFeed />,
						// Originally this was set to three different paths that each would show `Recognition.BonusFeed`.
						// What this meant, however, is that if the path changed it would totally unmount and then remount
						// the component tree. So, if you were on the home page and navigated to /invite from a button (which
						// should still show the home page) you'd see a flash, then the home page would load, followed by the
						// invite modal. This causes issues when multiple modals are already open, however, because handlers
						// and references added to the screen seem to be lost. So, what this does is just treat the three paths
						// as child routes but don't actually render anything for them. This allows the BonusFeed component to
						// remain mounted and visible while the user navigates between the different paths.
						children: [
							{
								path: '',
								element: null,
							},
							{
								path: 'invite',
								element: null,
							},
							{
								path: 'bonuses/:id',
								element: null,
							},
						],
					},
					{
						path: 'rewards/history',
						element: <Rewards.RewardsHistory />,
						loader: loadRewardsHistory,
					},
					{
						path: 'rewards',
						element: <Rewards.RewardsIndex />,
						loader: loadRewardsIndex,
						shouldRevalidate: ({ currentUrl, nextUrl }) => {
							if (!currentUrl || !nextUrl) {
								return true;
							}

							const currentPath = new URL(currentUrl).pathname;
							const nextPath = new URL(nextUrl).pathname;
							return currentPath !== nextPath;
						},
					},
					{
						path: 'bookmark',
						element: <Recognition.Bookmarks />,
					},
					{
						path: 'meet/*',
						element: <Meet.Meet />,
					},
					{
						path: 'growth/*',
						element: <Growth.Growth />,
					},
					{
						path: 'incentives/*',
						element: <Incentives.IncentiveSelect />,
						children: [
							{
								path: ':id',
								element: <Incentives.IncentiveDetails />,
							},
						],
					},
					{
						path: 'team/*',
						element: <Growth.TeamManagement />,
					},
					{
						path: 'company/users/:userId',
						element: <Profile.FlaggedProfilePage />,
					},
					{
						path: 'user-settings',
						element: (
							<FlaggedRoute variant="user-settings-redesign">
								<User.UserSettings />
							</FlaggedRoute>
						),
					},
					{
						path: 'bizy/history',
						element: (
							<FlaggedRoute variant={Variant.BizyConversationHistory}>
								<Bizy.BizyHistory />
							</FlaggedRoute>
						),
					},
					{
						path: 'mobile-unavailable',
						element: <MobileUnavailable />,
					},
					{ path: '*', element: <PageNotFound /> },
				],
			},
			// 404 page for the rest of the app
			{ path: '*', element: <PageNotFound /> },
		],
	},
]);

export const AppRouter: FC = () => <RouterProvider router={router} />;
